// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import {
    ChangeEvent,
    useCallback,
    useState,
} from "react";
import { find } from "../../../../../../../io/hyperify/core/functions/find";
import { map } from "../../../../../../../io/hyperify/core/functions/map";
import { some } from "../../../../../../../io/hyperify/core/functions/some";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { ClientDTO } from "../../../../../../../io/hyperify/core/store/types/client/ClientDTO";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { TranslationParams } from "../../../../../../../io/hyperify/core/types/TranslationParams";
import { Button } from "../../../../../../../io/hyperify/frontend/components/button/Button";
import { Form } from "../../../../../../../io/hyperify/frontend/components/form/Form";
import { AuthSessionData } from "../../../../../../../io/hyperify/frontend/hooks/useAuthSession";
import { useLogoutCallback } from "../../../../../../../io/hyperify/frontend/hooks/useLogoutCallback";
import { MY_SELECT_CLIENT_ARTICLE_CLASS_NAME } from "../../../../../core/constants/className";
import { SendanorApiPermission } from "../../../../../core/types/SendanorApiPermission";
import { useMyPermissionList } from "../../../../hooks/useMyPermissionList";
import { MyNewClientArticle } from "../newClient/MyNewClientArticle";
import { MyNoClientFoundArticle } from "./MyNoClientFoundArticle";
import { SelectAnyClientForm } from "./SelectAnyClientForm";
import "./MySelectClientArticle.scss";

const LOG = LogService.createLogger('MySelectClientArticle');

export enum NewClientType {
    NOT_SELECTED,
    COMPANY,
    PERSON
}

export interface MySelectClientArticleProps {
    readonly t: TranslationFunction;
    readonly className    ?: string;
    readonly client        : ClientDTO | undefined;
    readonly session       : AuthSessionData;
    readonly list          : readonly ClientDTO[];
    readonly selectClient  : (item: ClientDTO) => void;
    readonly title         : string;
    readonly selectAnyClientLabel      : string;
    readonly newCompanyButtonLabel      : string;
    readonly newPersonButtonLabel       : string;
    readonly reLoginButtonLabel         : string;
    readonly translationParams ?: TranslationParams;
}

export function MySelectClientArticle (props: MySelectClientArticleProps) {
    const t = props?.t;
    const className         = props?.className;
    const list              = props?.list;
    const title             = props?.title;
    const session           = props?.session;
    const selectClientProp  = props?.selectClient;
    const selectAnyClientLabel  = props?.selectAnyClientLabel;
    const translationParams  = props?.translationParams ?? {};
    const newCompanyButtonLabel = props?.newCompanyButtonLabel;
    const newPersonButtonLabel  = props?.newPersonButtonLabel;
    const reLoginButtonLabel    = props?.reLoginButtonLabel;
    const noResults          = (list?.length ?? 0) === 0;
    const hasPersonalAccount = some(list, (item : ClientDTO) => !item?.company);
    const [newClientType, setNewClientType] = useState<NewClientType>(NewClientType.NOT_SELECTED);
    const [permissions] = useMyPermissionList();
    const canSelectAnyClient : boolean = permissions ? permissions.includes(SendanorApiPermission.SELECT_ANY_CLIENT) : false;

    const selectClient = useCallback(
        (item: ClientDTO) => {
            if (selectAnyClientLabel) {
                LOG.debug(`Selected item: `, item);
                selectClientProp(item);
            } else {
                LOG.warn(`No "selectClient" prop defined.`);
            }
        },
        [
            selectAnyClientLabel,
            selectClientProp
        ]
    );

    const onRadioSelect = useCallback(
        (event : ChangeEvent<HTMLInputElement>) => {
            const itemId = event?.target?.value;
            if (itemId) {
                LOG.debug(`itemId = `, itemId);
                const item = find(list, (i) => i?.id === itemId);
                if (item) {
                    selectClient(item);
                } else {
                    LOG.error(`Could not find client by ID `, itemId);
                }
            } else {
                LOG.error(`Could not detect valid item id: `, itemId);
            }
        },
        [
            selectClient,
            list
        ]
    );

    const newClientSubmitAction = useCallback(
        (client: ClientDTO) => {
            LOG.debug(`New client: `, client);
            setNewClientType(NewClientType.NOT_SELECTED);
            selectClient(client);
        },
        [
            setNewClientType,
            selectClient
        ]
    );

    const logoutCallback = useLogoutCallback('MySelectClientArticle');

    if (newClientType !== NewClientType.NOT_SELECTED) {
        return (
            <MyNewClientArticle
                className={`${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}${className ? ' ' + className : ''}`}
                t={t}
                session={session}
                isCompany={newClientType === NewClientType.COMPANY}
                backAction={() => {
                    setNewClientType(NewClientType.NOT_SELECTED);
                }}
                submitAction={newClientSubmitAction}
            />
        );
    }

    if (noResults) {
        return (
            <MyNoClientFoundArticle
                className={`${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}${className ? ' ' + className : ''}`}
                t={t}
                session={session}
                foundClient={selectClient}
            >

                <SelectAnyClientForm
                    className={`${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}-form ${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}-select-client-form`}
                    selectAnyClientLabel={selectAnyClientLabel}
                    selectClient={selectClient}
                />

            </MyNoClientFoundArticle>
        );
    }

    return (
        <article
            className={
                MY_SELECT_CLIENT_ARTICLE_CLASS_NAME
                + (className ? ` ${className}` : '')
            }
        >
            <Form className={`${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}-form`}>

                <h3>{title}</h3>

                <div className={`${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}-fieldset`}>
                    {map(list, (item : ClientDTO) => (
                        <label
                            key={`radio-${item.id}`}
                            className={`${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}-radio-label`}
                        >
                            <input type={"radio"}
                                   name={"client-id"}
                                   value={item.id}
                                   onChange={onRadioSelect}
                            />&nbsp;
                            {item.companyCode || item.company ? (
                                <>{item.company}{item?.companyCode ? <>, {item.companyCode}</>: null} (#{item.id})</>
                            ) : (
                                <>{item.firstname} {item.lastname} (#{item.id})</>
                            )}
                        </label>
                    ))}
                </div>

                <div className={`${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}-new-client-buttons`}>

                    <Button
                        className={"button"}
                        click={() => setNewClientType(NewClientType.COMPANY)}
                    >{t(newCompanyButtonLabel, translationParams)}</Button>

                    {!hasPersonalAccount ? (
                        <Button
                            className={"button"}
                            click={() => setNewClientType(NewClientType.PERSON)}
                        >{t(newPersonButtonLabel, translationParams)}</Button>
                    ): null}

                    <Button
                        className={"button"}
                        click={logoutCallback}
                    >{t(reLoginButtonLabel, translationParams)}</Button>

                </div>

            </Form>

            {canSelectAnyClient ? (
                <SelectAnyClientForm
                    className={`${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}-form ${MY_SELECT_CLIENT_ARTICLE_CLASS_NAME}-select-client-form`}
                    selectAnyClientLabel={selectAnyClientLabel}
                    selectClient={selectClient}
                />
            ) : null}

        </article>
    );

}

